"use client"

import { Link } from "react-router-dom"
import { ArrowRight, Package } from "lucide-react"
import type { Category } from "@/lib/api"
import { useLanguage } from "@/lib/language-context"
import { cn } from "@/lib/utils"

interface CategoryGridProps {
  categories: Category[]
  limit?: number
}

function getImageUrl(category: Category): string {
  if (category.image_url) return category.image_url
  if (category.image) return category.image
  return `/placeholder.svg?height=400&width=400&query=${encodeURIComponent(category.name + " herbal natural medicine")}`
}

export function CategoryGrid({ categories, limit = 9 }: CategoryGridProps) {
  const { t } = useLanguage()

  const safeCategories = categories || []
  const displayCategories = safeCategories.slice(0, limit)

  if (safeCategories.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-primary/10 flex items-center justify-center animate-pulse">
          <Package className="w-12 h-12 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-3">{t("home", "no_categories")}</h3>
        <p className="text-muted-foreground max-w-md mx-auto">{t("home", "no_categories_desc")}</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
      {displayCategories.map((category, index) => (
        <Link
          key={category.id}
          to={`/categories/${category.slug}`}
          className={cn(
            "group relative overflow-hidden rounded-2xl lg:rounded-3xl bg-card border border-border/50",
            "transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2",
            "card-shine",
            index === 0 && "lg:col-span-2 lg:row-span-2",
          )}
        >
          <div
            className={cn("relative overflow-hidden", index === 0 ? "aspect-[4/3] lg:aspect-square" : "aspect-[4/3]")}
          >
            {/* Image with zoom effect */}
            <div className="absolute inset-0 img-zoom">
              <img
                src={getImageUrl(category) || "/placeholder.svg"}
                alt={category.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

            {/* Shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -translate-x-full group-hover:translate-x-full" />

            {/* Content */}
            <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end">
              <div className="transform transition-all duration-500">
                {/* Product count badge */}
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full mb-3 shadow-lg">
                  <Package className="w-3 h-3" />
                  {category.products_count} {t("home", "products_count")}
                </span>

                {/* Category name */}
                <h3
                  className={cn(
                    "font-bold text-white mb-2 text-balance",
                    index === 0 ? "text-xl sm:text-2xl lg:text-3xl" : "text-lg sm:text-xl",
                  )}
                >
                  {category.name}
                </h3>

                {/* View link - appears on hover */}
                <div className="flex items-center gap-2 text-white/90 text-sm font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <span>{t("home", "view_category")}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
